import { GravityConfig, SimulationConfig, CameraConfig, CameraMode } from './types';

// ============================================
// Physics Constants
// ============================================

// Scaled gravitational constant for visual simulation
// Real G = 6.674e-11, but we use scaled values for better visualization
export const DEFAULT_GRAVITY_CONFIG: GravityConfig = {
  G: 50, // Moderate gravity for visible orbits
  enabled: true,
  softening: 0.5, // Prevents division by zero at close range
};

// ============================================
// Simulation Constants
// ============================================

export const DEFAULT_SIMULATION_CONFIG: SimulationConfig = {
  timeScale: 0.5, // Moderate speed for action and observation
  paused: false,
  gravity: DEFAULT_GRAVITY_CONFIG,
  collisionsEnabled: true,
};

// ============================================
// Camera Constants
// ============================================

export const DEFAULT_CAMERA_CONFIG: CameraConfig = {
  mode: 'ship' as CameraMode,
  fov: 60,
  near: 0.1,
  far: 100000,
  orbitDistance: 500,
  orbitMinDistance: 10,
  orbitMaxDistance: 10000,
  orbitSpeed: 0.005,
  flySpeed: 100,
  followDistance: 50,
  dampingFactor: 0.1,
};

// ============================================
// Input Constants
// ============================================

export const INPUT_CONFIG = {
  // Drag detection threshold (pixels)
  dragThreshold: 5,

  // Long press duration (ms)
  longPressDuration: 500,

  // Double tap max interval (ms)
  doubleTapInterval: 300,

  // Pinch detection - minimum distance change
  pinchThreshold: 10,

  // Mouse wheel zoom sensitivity
  wheelZoomSpeed: 0.001,

  // Touch zoom sensitivity
  touchZoomSpeed: 0.01,

  // Keyboard pan speed (units per second)
  keyPanSpeed: 200,

  // Mouse rotation sensitivity
  rotationSpeed: 0.003,
};

// ============================================
// Visual Constants
// ============================================

export const COLORS = {
  // Space background
  background: 0x000011,

  // Star colors
  starYellow: 0xffff00,
  starOrange: 0xff8800,
  starRed: 0xff4400,
  starBlue: 0x4488ff,
  starWhite: 0xffffff,

  // Planet colors
  earthBlue: 0x4488ff,
  marsRed: 0xcc4422,
  venusYellow: 0xddcc88,
  jupiterOrange: 0xdd8844,
  saturnGold: 0xddaa44,
  neptuneBlue: 0x4466dd,

  // UI colors
  selectionHighlight: 0x00ff00,
  orbitPath: 0x444466,
  velocityVector: 0xff0000,

  // Asteroid
  asteroidGray: 0x888888,
};

// ============================================
// Default Celestial Bodies
// ============================================

export const SOLAR_SYSTEM_SCALE = {
  // Distance scale (1 unit = X km)
  distance: 1000000,

  // Size scale (radius multiplier for visibility)
  size: 50,

  // Mass scale
  mass: 1e-20,
};

// ============================================
// Rendering Constants
// ============================================

export const RENDER_CONFIG = {
  // Star field
  starFieldCount: 5000,
  starFieldRadius: 50000,

  // Orbit trail
  orbitTrailLength: 500,
  orbitTrailOpacity: 0.3,

  // Level of detail thresholds
  lodHigh: 100,
  lodMedium: 500,
  lodLow: 2000,

  // Shadow map
  shadowMapSize: 2048,
};

// ============================================
// Key Bindings
// ============================================

export const KEY_BINDINGS = {
  // Movement (fly mode)
  forward: ['KeyW', 'ArrowUp'],
  backward: ['KeyS', 'ArrowDown'],
  left: ['KeyA', 'ArrowLeft'],
  right: ['KeyD', 'ArrowRight'],
  up: ['KeyE'],
  down: ['KeyQ', 'ShiftLeft'],

  // Camera
  toggleCameraMode: ['KeyC'],
  focusSelected: ['KeyF'],
  resetCamera: ['KeyR'],

  // Simulation
  togglePause: ['KeyP'],
  speedUp: ['Period', 'Equal'],
  slowDown: ['Comma', 'Minus'],

  // Selection
  deselect: ['Escape'],
  delete: ['Delete', 'Backspace'],

  // Weapons
  fire: ['Space'],
};
